import React from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Checkmark, Close, InformationFilled } from '@mint-lib/icons';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Divider,
  FeaturedIcon,
  typographyVariants,
} from '@mint-lib/tw-components';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const RulesDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <Dialog modal onOpenChange={onClose} open={isOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot aria-describedby={undefined} size="lg">
          <DialogHeader>
            <DialogTitle>{t('Uploading rules')}</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <div className="flex flex-col gap-8">
              <Alert severity="info" variant="multiline">
                <InformationFilled />
                <p className={typographyVariants({ variant: 'bodyShort02' })}>
                  {t(
                    "All materials stored in the knowledge database will be saved in the client's designated workspace and will remain the exclusive intellectual property of the client",
                  )}
                </p>
              </Alert>
              <div className="flex justify-evenly gap-10">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <FeaturedIcon variant="success" icon={<Checkmark />} />
                    <p className={typographyVariants({ variant: 'h3' })}>
                      {t('What to upload')}
                    </p>
                  </div>

                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Audience Data & Insights:')}
                    </p>
                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t('Target demographics, psychographics, geo-targeting.')}
                    </p>
                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t(
                        'Behavioral trends (e.g., channel preferences, conversion habits).',
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Product Briefs:')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t('Clear explanation of the product/service.')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t(
                        'Features, benefits, pricing, technical specifications.',
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Mediaplan Briefs:')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t('High-level strategy outlines.')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t(
                        'Communication frameworks, messaging pillars, tone of voice.',
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Market & Competitor Intelligence:')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t('Industry trends, benchmarks.')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t('Competitor analyses and positioning.')}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Creative & Content Guidelines:')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t(
                        'Brand voice, design rules, platform-specific best practices.',
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Platform Insights:')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t('Algorithm details, performance optimization tips.')}
                    </p>
                  </div>
                </div>
                <Divider orientation="vertical" />
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <FeaturedIcon variant="error" icon={<Close />} />
                    <p className={typographyVariants({ variant: 'h3' })}>
                      {t('What NOT to upload')}
                    </p>
                  </div>

                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Screenshots of Dashboards:')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t('Visuals of reporting tools or BI dashboards.')}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className={typographyVariants({ variant: 'h6' })}>
                      {t('Existing Analytics Data:')}
                    </p>

                    <p
                      className={typographyVariants({ variant: 'bodyShort01' })}
                    >
                      {t(
                        'Performance data already available in the Mint platform.',
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button variant="contained" onClick={onClose}>
              {t('Got it')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { RulesDialog };
